/**
 * CategorySidebar — category filter, now rendered as horizontal tabs.
 *
 * brief-13-shell-nav-rail: converted from a vertical aside to a horizontal,
 * scrollable tab row per the shell decision record (2026-07-25 §4). Keeps
 * the component name and prop contract so MarketplacePage doesn't need to
 * know the layout changed; only the rendered markup and CSS classes differ.
 *
 * WP-SL-V3: derives categories from actual tile tags (API-sourced) rather
 * than a hardcoded list. "All" is always first. In vertical-ref mode the
 * tiles prop is populated once the catalog loads; the sidebar updates reactively.
 */

import { useState } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';

interface CategorySidebarProps {
  tiles: TileConfig[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
}

function deriveCategories(tiles: TileConfig[]): string[] {
  const tagSet = new Set<string>();
  for (const tile of tiles) {
    for (const tag of tile.tags) tagSet.add(tag);
  }
  return ['All', ...Array.from(tagSet).sort()];
}

export default function CategorySidebar({ tiles, activeCategory, onCategoryChange }: CategorySidebarProps) {
  const [search, setSearch] = useState('');

  const categories = deriveCategories(tiles);

  const countFor = (cat: string) =>
    cat === 'All'
      ? tiles.length
      : tiles.filter((tile) => tile.tags.includes(cat)).length;

  const filtered = categories.filter(
    (c) => !search || c.toLowerCase().includes(search.toLowerCase())
  );

  // Past ~15 entries a scrollable tab row gets crowded — fall back to the
  // search box inline so the row itself doesn't need to grow (decision doc
  // note: "horizontal category tabs — revisit past ~15 catalogue entries").
  const showSearch = categories.length > 15;

  return (
    <div className="dp-cat-tabs__wrap">
      {showSearch && (
        <input
          type="search"
          placeholder={t('sidebar.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="dp-cat-tabs__search"
        />
      )}

      <div className="dp-cat-tabs__scroll" role="tablist" aria-label={t('sidebar.eyebrow')}>
        {filtered.map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`dp-cat-tab${isActive ? ' dp-cat-tab--active' : ''}`}
              onClick={() => onCategoryChange(cat)}
            >
              <span>{cat}</span>
              <span className="dp-cat-tab__count">{countFor(cat)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
